import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { insertFeatureRequestSchema, type InsertFeatureRequest } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useSEO } from "@/hooks/use-seo";
import { seoFor } from "@/data/marketing-routes";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Lightbulb, Zap, Users, Loader2, CheckCircle, ArrowRight } from "lucide-react";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";

export default function FeatureRequestsPage() {
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  useSEO(seoFor("/feature-requests"));

  const form = useForm<InsertFeatureRequest>({
    resolver: zodResolver(insertFeatureRequestSchema),
    defaultValues: {
      name: "",
      email: "",
      description: "",
      roomsManaged: undefined,
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: InsertFeatureRequest) => {
      await apiRequest("POST", "/api/feature-requests", data);
    },
    onSuccess: () => {
      setSubmitted(true);
      form.reset();
    },
    onError: () => {
      toast({
        title: "Something went wrong",
        description: "Could not send your request. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: InsertFeatureRequest) => {
    mutation.mutate(data);
  };

  const infoCards = [
    { icon: Lightbulb, title: "Share Your Ideas", description: "Got a brilliant concept for a new feature? We're all ears!" },
    { icon: Zap, title: "Solve Problems", description: "Facing a recurring issue? Help us understand it so we can build a solution." },
    { icon: Users, title: "Community Driven", description: "Your feedback directly influences our development roadmap." },
  ];

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <PublicHeader />
      <div className="bg-gradient-to-br from-[#0a1628] via-[#0d2447] to-brand">
        <section className="pt-28 md:pt-32 pb-16 md:pb-24">
          <div className="container mx-auto px-6 text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight tracking-tight" data-testid="text-feature-heading">
              Shape the Future of PadPulse
            </h1>
            <p className="text-lg md:text-xl text-blue-100 max-w-2xl mx-auto" data-testid="text-feature-subtitle">
              Your insights drive our innovation. Tell us about the challenges you face or the features you dream of.
            </p>
          </div>
        </section>
      </div>

      <main className="flex-grow">
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-6">
            <div className="grid md:grid-cols-3 gap-5 max-w-4xl mx-auto mb-14">
              {infoCards.map((card) => (
                <Card key={card.title} className="p-6 flex flex-col items-center text-center border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300" data-testid={`card-info-${card.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="w-12 h-12 rounded-xl bg-brand-accent/10 flex items-center justify-center mb-4">
                    <card.icon className="text-brand-accent" size={24} />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-2">{card.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{card.description}</p>
                </Card>
              ))}
            </div>

            <div className="max-w-2xl mx-auto">
              {submitted ? (
                <Card className="p-8 md:p-10 text-center border border-gray-100 shadow-sm" data-testid="feature-success">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-7 w-7 text-emerald-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">Thank You!</h2>
                  <p className="text-gray-500 mb-6">Your feature request has been successfully submitted. Our team will review it carefully.</p>
                  <Button
                    onClick={() => setSubmitted(false)}
                    className="bg-brand-accent hover:bg-[#e69500] text-white font-semibold"
                    data-testid="button-submit-another"
                  >
                    Submit Another Request
                  </Button>
                </Card>
              ) : (
                <Card className="p-8 md:p-10 border border-gray-100 shadow-sm">
                  <h2 className="text-2xl font-bold text-center text-gray-900 mb-6" data-testid="text-form-heading">
                    Got a Burning Problem or Bright Idea?
                  </h2>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-700 text-sm">Your Name</FormLabel>
                            <FormControl>
                              <Input placeholder="e.g. Alex Smith" className="border-gray-200" {...field} data-testid="input-feature-name" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-700 text-sm">Your Email</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="you@example.com" className="border-gray-200" {...field} data-testid="input-feature-email" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="roomsManaged"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-700 text-sm">Number of Rooms you own/manage</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min="1"
                                placeholder="e.g. 10"
                                className="border-gray-200"
                                {...field}
                                onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                                value={field.value ?? ""}
                                data-testid="input-feature-rooms"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-700 text-sm">Describe Your Problem or Feature Request</FormLabel>
                            <FormControl>
                              <Textarea
                                className="min-h-[120px] border-gray-200"
                                placeholder="e.g., 'It would be great if PadPulse could automatically...' or 'I'm struggling with X, and a feature that does Y would help...'"
                                {...field}
                                data-testid="input-feature-description"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="submit"
                        className="w-full bg-brand-accent hover:bg-[#e69500] text-white font-semibold shadow-lg shadow-orange-500/20"
                        disabled={mutation.isPending}
                        data-testid="button-submit-feature"
                      >
                        {mutation.isPending ? (
                          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting...</>
                        ) : (
                          <>Send My Suggestion <ArrowRight size={16} className="ml-2" /></>
                        )}
                      </Button>
                    </form>
                  </Form>
                </Card>
              )}
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
